import { useMemo, useState } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { Search, AttachFile } from "@mui/icons-material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { EmptyState, LoadingPanel, ErrorPanel } from "./ui";
import usePartnerQuery from "../hooks/usePartnerQuery";
import AttachmentItem from "components/Attachments/AttachmentItem";

const PAGE_SIZE = 50;

/** Published reference data barely moves: remounts within five minutes reuse the cache. */
const REFERENCE_STALE_TIME = 5 * 60 * 1000;

/**
 * Read-only org reference data (price list, checklists) laid out as cards: the short labelled
 * values sit in a field grid, and the authored rich text stays folded away until asked for, so a
 * long requirements list never buries the charges next to it.
 */
const ReferenceBoard = ({
  queryKey,
  fetcher,
  searchLabel,
  emptyTitle,
  emptyDescription,
  itemNoun = "entry",
  itemNounPlural = "entries",
  notesToggleLabel = "Show details",
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [openNotes, setOpenNotes] = useState([]);

  // Free text fires a request per keystroke without this; the input stays instant while the
  // query waits for a pause. Same 400ms pattern as the courses catalogue.
  const debouncedSearch = useDebouncedValue(search.trim(), 400);

  const params = useMemo(
    () => ({ page, size: PAGE_SIZE, ...(debouncedSearch ? { search: debouncedSearch } : {}) }),
    [page, debouncedSearch]
  );

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    queryKey(params),
    () => fetcher(params),
    { keepPreviousData: true, staleTime: REFERENCE_STALE_TIME }
  );

  const items = data?.content || [];
  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalItems ?? items.length;

  const toggleNotes = (id) =>
    setOpenNotes((current) =>
      current.includes(id) ? current.filter((open) => open !== id) : [...current, id]
    );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <TextField
          size="small"
          label={searchLabel}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 280, backgroundColor: "white", borderRadius: "12px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" className="text-slate-400" />
              </InputAdornment>
            ),
          }}
        />
        {!isLoading && !isError ? (
          <p className="text-xs text-slate-500 m-0">
            {totalItems} {totalItems === 1 ? itemNoun : itemNounPlural}
            {debouncedSearch ? ` matching "${debouncedSearch}"` : ""}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <LoadingPanel />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={search ? "Nothing matches that search" : emptyTitle}
          description={search ? "Try a different word." : emptyDescription}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {items.map((item) => (
              <ReferenceCard
                key={item.id}
                item={item}
                notesOpen={openNotes.includes(item.id)}
                notesToggleLabel={notesToggleLabel}
                onToggleNotes={() => toggleNotes(item.id)}
              />
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 mt-4">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const isUrlValue = (value) => /^https?:\/\/\S+/i.test(String(value || "").trim());

const renderFactValue = (fact) => {
  const value = fact.value ?? "—";
  if (isUrlValue(value)) {
    const href = String(value).trim();
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        title={href}
        className="font-medium text-teal-700 hover:underline [overflow-wrap:anywhere] break-all"
      >
        {href}
      </a>
    );
  }
  return (
    <span title={String(value)} className="[overflow-wrap:anywhere] break-words">
      {value}
    </span>
  );
};

const ReferenceCard = ({ item, notesOpen, notesToggleLabel, onToggleNotes }) => {
  const facts = item.facts || [];
  const notes = item.notes || [];
  const attachments = item.attachments || [];
  return (
    <article className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <header className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900 m-0 break-words [overflow-wrap:anywhere]">
          {item.title || "Untitled"}
        </h3>
        {item.subtitle && item.subtitle !== item.title ? (
          <p className="text-xs text-slate-500 m-0 mt-0.5 break-words [overflow-wrap:anywhere]">
            {item.subtitle}
          </p>
        ) : null}
      </header>

      {facts.length > 0 ? (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100 m-0">
          {facts.map((fact) => (
            <div key={fact.label} className="bg-white px-4 py-3 min-w-0">
              <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0">
                {fact.label}
              </dt>
              <dd className="text-sm font-semibold text-slate-900 m-0 mt-0.5 tabular-nums whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {renderFactValue(fact)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {attachments.length > 0 ? (
        <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <AttachmentItem
              key={attachment.url}
              url={attachment.url}
              renderFile={() => (
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-700 no-underline hover:bg-slate-100"
                >
                  <AttachFile sx={{ fontSize: 14 }} className="text-slate-400" />
                  {attachment.name}
                </a>
              )}
            />
          ))}
        </div>
      ) : null}

      {notes.length > 0 ? (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={onToggleNotes}
            aria-expanded={notesOpen}
            className="bg-transparent border-0 p-0 text-xs font-semibold text-teal-700 cursor-pointer"
          >
            {notesOpen ? "Hide details" : `${notesToggleLabel} (${notes.length})`}
          </button>
          {notesOpen ? (
            <div className="mt-3 space-y-3">
              {notes.map((note) => (
                <div key={note.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 m-0 mb-1">
                    {note.label}
                  </p>
                  <div
                    className="reference-note text-xs text-slate-600 leading-relaxed"
                    // Requirements and notes are authored as rich text in the admin form.
                    dangerouslySetInnerHTML={{ __html: note.html }}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};

export default ReferenceBoard;
